import React, { useEffect, useState } from 'react';
import { surveyAPI } from '@/api/api';
import { toast } from 'sonner';
import {
  ClipboardList, Copy, Check, Loader2, Sparkles, TrendingUp, Smile, Meh, Frown,
  Star, ChevronDown, ChevronUp, ThumbsUp, AlertTriangle, ListChecks, Users,
} from 'lucide-react';

const sentimentStyle = {
  Positive: { icon: Smile, cls: 'bg-emerald-100 text-emerald-700', bar: 'bg-emerald-500' },
  Neutral: { icon: Meh, cls: 'bg-amber-100 text-amber-700', bar: 'bg-amber-500' },
  Negative: { icon: Frown, cls: 'bg-red-100 text-red-700', bar: 'bg-red-500' },
};

const StatCard = ({ label, value, sub, icon: Icon, tint }) => (
  <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium text-slate-500">{label}</span>
      <Icon className={`h-5 w-5 ${tint}`} />
    </div>
    <div className="mt-2 text-3xl font-bold text-slate-900">{value}</div>
    {sub && <div className="mt-1 text-xs text-slate-400">{sub}</div>}
  </div>
);

const StudentSurveysPage = () => {
  const [link, setLink] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [responses, setResponses] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [ai, setAi] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [l, a, r] = await Promise.all([
          surveyAPI.getLink(),
          surveyAPI.getAnalytics(),
          surveyAPI.getResponses(),
        ]);
        setLink(`${window.location.origin}/survey`);
        setAnalytics(a.data);
        setResponses(r.data.responses || []);
        setQuestions(r.data.questions || []);
      } catch (e) {
        toast.error('Could not load survey data');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const copyLink = () => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Survey link copied — broadcast it to your students!');
    setTimeout(() => setCopied(false), 2000);
  };

  const generateAi = async () => {
    setAiLoading(true);
    try {
      const res = await surveyAPI.getAiSummary();
      setAi(res.data);
    } catch (e) {
      toast.error('Could not generate AI analysis');
    } finally {
      setAiLoading(false);
    }
  };

  const qLabel = (id) => (questions.find((q) => q.id === id) || {}).label || id;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const dist = analytics?.sentiment_distribution || { Positive: 0, Neutral: 0, Negative: 0 };
  const total = analytics?.total_responses || 0;

  return (
    <div className="space-y-6" data-testid="student-surveys-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <ClipboardList className="h-6 w-6 text-blue-600" /> Student Experience Surveys
        </h1>
        <p className="text-slate-500 mt-1">Broadcast the survey link and let AI surface how your students really feel.</p>
      </div>

      {/* Broadcast link */}
      <div className="rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white shadow-md">
        <div className="flex items-center gap-2 text-sm font-semibold opacity-90 mb-2">
          <Users className="h-4 w-4" /> Broadcast Link
        </div>
        <p className="text-sm opacity-80 mb-3">Share this link with students via WhatsApp, email or notice board. No login needed.</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            data-testid="survey-link-input"
            readOnly
            value={link}
            className="flex-1 rounded-lg bg-white/15 px-4 py-2.5 text-sm text-white placeholder-white/60 ring-1 ring-white/30 focus:outline-none"
          />
          <button
            data-testid="copy-survey-link-button"
            onClick={copyLink}
            className="flex items-center justify-center gap-2 rounded-lg bg-white px-5 py-2.5 text-sm font-semibold text-blue-700 hover:bg-blue-50 transition-colors"
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? 'Copied' : 'Copy Link'}
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Total Responses" value={total} icon={Users} tint="text-blue-500" />
        <StatCard label="Positive" value={dist.Positive} sub={total ? `${Math.round((dist.Positive / total) * 100)}% of responses` : ''} icon={Smile} tint="text-emerald-500" />
        <StatCard label="Needs Attention" value={dist.Negative} sub={total ? `${Math.round((dist.Negative / total) * 100)}% of responses` : ''} icon={Frown} tint="text-red-500" />
      </div>

      {/* Sentiment distribution bar */}
      {total > 0 && (
        <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
          <div className="text-sm font-semibold text-slate-700 mb-3">Sentiment Distribution</div>
          <div className="flex h-4 w-full overflow-hidden rounded-full bg-slate-100">
            {['Positive', 'Neutral', 'Negative'].map((s) => {
              const pct = total ? (dist[s] / total) * 100 : 0;
              return pct > 0 ? (
                <div key={s} className={sentimentStyle[s].bar} style={{ width: `${pct}%` }} title={`${s}: ${dist[s]}`} />
              ) : null;
            })}
          </div>
          <div className="mt-3 flex gap-4 text-xs text-slate-500">
            {['Positive', 'Neutral', 'Negative'].map((s) => (
              <span key={s} className="flex items-center gap-1.5">
                <span className={`h-2.5 w-2.5 rounded-full ${sentimentStyle[s].bar}`} /> {s} ({dist[s]})
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Average ratings */}
      {analytics?.average_ratings?.length > 0 && (
        <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
          <div className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-blue-500" /> Average Ratings by Area
          </div>
          <div className="space-y-3">
            {analytics.average_ratings.map((r) => (
              <div key={r.id}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">{r.label}</span>
                  <span className="font-semibold text-slate-800">{r.average}/5</span>
                </div>
                <div className="h-2 w-full rounded-full bg-slate-100">
                  <div className="h-2 rounded-full bg-blue-500" style={{ width: `${(r.average / 5) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI analysis */}
      <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-indigo-500" /> AI Management Analysis
          </div>
          <button
            data-testid="generate-ai-summary-button"
            onClick={generateAi}
            disabled={aiLoading || total === 0}
            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {ai ? 'Regenerate' : 'Generate Analysis'}
          </button>
        </div>

        {!ai && !aiLoading && (
          <p className="text-sm text-slate-400">
            {total === 0 ? 'No responses yet. Share the link to start collecting feedback.' : 'Click "Generate Analysis" to get AI-powered insights and action items from all responses.'}
          </p>
        )}

        {ai && (
          <div className="space-y-4" data-testid="ai-summary-content">
            <p className="rounded-lg bg-indigo-50 p-4 text-sm text-slate-700">{ai.summary}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { title: 'Strengths', items: ai.strengths, icon: ThumbsUp, tint: 'text-emerald-600', bg: 'bg-emerald-50' },
                { title: 'Concerns', items: ai.concerns, icon: AlertTriangle, tint: 'text-amber-600', bg: 'bg-amber-50' },
                { title: 'Action Items', items: ai.action_items, icon: ListChecks, tint: 'text-blue-600', bg: 'bg-blue-50' },
              ].map((col) => (
                <div key={col.title} className={`rounded-lg ${col.bg} p-4`}>
                  <div className={`flex items-center gap-2 text-sm font-semibold ${col.tint} mb-2`}>
                    <col.icon className="h-4 w-4" /> {col.title}
                  </div>
                  <ul className="space-y-1.5 text-sm text-slate-600">
                    {(col.items || []).length === 0 && <li className="text-slate-400">—</li>}
                    {(col.items || []).map((it, i) => (
                      <li key={i} className="flex gap-2"><span className={col.tint}>•</span>{it}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            {ai.engine === 'rule-based' && (
              <p className="text-xs text-amber-600">AI engine unavailable — showing basic summary only.</p>
            )}
          </div>
        )}
      </div>

      {/* Responses */}
      <div className="rounded-xl bg-white shadow-sm ring-1 ring-slate-200">
        <div className="border-b border-slate-100 px-5 py-4 text-sm font-semibold text-slate-700">
          Individual Responses ({responses.length})
        </div>
        {responses.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-slate-400">No responses yet.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {responses.map((resp) => {
              const s = resp.sentiment?.overall_sentiment || 'Neutral';
              const St = sentimentStyle[s] || sentimentStyle.Neutral;
              const isOpen = expanded === resp.id;
              return (
                <div key={resp.id} data-testid={`survey-response-${resp.id}`}>
                  <button
                    onClick={() => setExpanded(isOpen ? null : resp.id)}
                    className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${St.cls}`}>
                        <St.icon className="h-3.5 w-3.5" /> {s}
                      </span>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-slate-800">{resp.student_name || 'Anonymous'}{resp.program ? ` · ${resp.program}` : ''}</div>
                        <div className="text-xs text-slate-400">{resp.branch_name} · {new Date(resp.created_at).toLocaleDateString()}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {isOpen ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                    </div>
                  </button>
                  {isOpen && (
                    <div className="bg-slate-50 px-5 py-4 space-y-3">
                      {resp.sentiment?.summary && (
                        <p className="rounded-lg bg-white p-3 text-sm italic text-slate-600 ring-1 ring-slate-200">
                          <Sparkles className="mr-1 inline h-3.5 w-3.5 text-indigo-500" />{resp.sentiment.summary}
                        </p>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                        {questions.map((q) => {
                          const v = resp.answers?.[q.id];
                          if (v === undefined || v === '' || v === null) return null;
                          return (
                            <div key={q.id} className="text-sm">
                              <div className="text-xs font-medium text-slate-500">{qLabel(q.id)}</div>
                              <div className="text-slate-800">{q.type === 'rating' ? `${v}/5` : v}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentSurveysPage;
